import React, { useState, useRef } from "react";
import { getPrecision } from "../../../../utils/tillHelpers";
import { Row, Col, Button, Input, Space, message } from "antd";
import sortBy from "lodash/sortBy";
import Back from "../../../../assets/images/backIcon.svg";
import CancelArrow from "../../../../assets/images/cancelArrow.svg";
import Cash1 from "../../../../assets/images/cash.svg";
import Card1 from "../../../../assets/images/card.svg";
import Credit from "../../../../assets/images/creditIcon.svg";
import Loyality from "../../../../assets/images/loyalityIcon.svg";
import Gift from "../../../../assets/images/giftIcon.svg";
import giftVoucher from "../../../../assets/images/giftVoucher.svg";
import creditNote from "../../../../assets/images/creditNote.svg";
import qrCodeIcon from "../../../../assets/images/qrCodeIcon.svg";
import dayjs from "dayjs";
import { getMultilingualText } from "../../../../utils/getMultilingualText";
import { useWindowDimensions } from "../../../../lib/useViewport";
import messages from "../../../../errors/messages.en-IN.json";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import useCartStore from "../../../../stores/useCartStore";
import PaymentDataPopup from "../../Core/payment/PaymentDataPopup";
import "../restaurantStyle.css";
import { KEYPAD_NO_FOCUS } from "../../../../lib/keypadNoFocus";

const PaymentCheckout = ({
  cart,
  setCart,
  paymentVisible,
  setPaymentVisible,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setIsCardPaymentFlag,
  amount,
  setAmount,
  onChangeAmount,
  paymentModalInputRef,
  setIsInputFocused,
  requestPayment,
  handleCashPayment,
  handleAmount,
  setNumb,
  setKeyValue,
  filteredPaymentMethods,
  denaminationsKeyboard,
  setDenaminationsKeyboard,
  handleOrderCompletion,
  processOrder,
  setShowOrderCompleteFlag,
  userPreferences,
  tillData,
  posConfig,
  selectedPayment,
  setSelectedPayment,
  markTableBillRequested,
  restaurantButtonLoading,
  setRestaurantButtonLoading,
}) => {
  const { width } = useWindowDimensions();
  const [showAll, setShowAll] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [isShowingAll, setIsShowingAll] = useState(false);

  // BUG-2103: single source of price precision for this component. Previously the
  // overpayment gate and the amount-due display hard-coded toFixed(2) while a third
  // site re-derived prcPrecision locally — on a 3-decimal currency (KWD/BHD/OMR) the
  // hard-coded pair truncated the total by up to 0.005 and rejected exact tenders.
  const prcPrecision = getPrecision(tillData);
  const currenciesList = tillData?.tillAccess.csBunit.currencies;
  const currenciesData = tillData?.tillAccess?.csBunit?.currencies[0]?.denominations?.sort((a, b) => b.value - a.value) || [];
  const themeJSON =
    posConfig?.themeJSON && posConfig?.themeJSON !== "N" && Object.keys(posConfig?.themeJSON).length > 0
      ? JSON.parse(posConfig?.themeJSON)?.layout4
      : {};

  const initialShowPayments =
    filteredPaymentMethods.length > 4
      ? sortBy(filteredPaymentMethods, "sequenceNo").slice(0, 3)
      : sortBy(filteredPaymentMethods, "sequenceNo");
  const [showPayments, setShowPayments] = useState(initialShowPayments);
  const [endIndex, setEndIndex] = useState(initialShowPayments.length);
  const [isProcessing, setIsProcessing] = useState(false);
  // isProcessing state drives the button's disabled/greyed styling below, but it
  // cannot be the double-submit guard on its own: setIsProcessing(true) does not
  // update the captured `isProcessing` closure synchronously, so two invocations
  // landing in the same tick (touchscreen double-tap, or queued clicks replaying
  // after a blocked main thread — the "system hung and the user retried" case
  // behind the duplicate-payment-receipt reports) both read false and both charge.
  // The ref is the real guard; the state stays purely for rendering.
  const paymentInFlightRef = useRef(false);
  // TSK-2934: generic payment-data popup — local to this screen since the
  // post-payment tail (markTableBillRequested/handleOrderCompletion) is
  // Restaurant-specific and not shared with the Retail flow.
  const [paymentDataPopupOpen, setPaymentDataPopupOpen] = useState(false);

  const showMoreItems = () => {
    const sortedData = sortBy(filteredPaymentMethods, "sequenceNo");
    const totalItems = sortedData.length;
    if (totalItems <= 4) return;
    let newStartIndex = endIndex;
    let newEndIndex = newStartIndex + 3;
    if (newStartIndex >= totalItems) {
      newStartIndex = 0;
      newEndIndex = 3;
    } else if (newEndIndex > totalItems) {
      newEndIndex = totalItems;
    }
    setShowPayments(sortedData.slice(newStartIndex, newEndIndex));
    setStartIndex(newStartIndex);
    setEndIndex(newEndIndex);
    // setIsShowingAll(true);
  };

  const showPreviousItems = () => {
    const sortedData = sortBy(filteredPaymentMethods, "sequenceNo").slice(0, 3);
    setShowPayments(sortedData);
    setStartIndex(0);
    setEndIndex(sortedData.length);
    setIsShowingAll(false);
  };

  const handleEnterPayment = async () => {
    if (paymentInFlightRef.current || isProcessing || restaurantButtonLoading) return;

    if (posConfig?.disableOfflineBilling === "Y" && !navigator.onLine) {
      message.warning("Network is unavailable. Please check your connection before completing the bill.");
      return;
    }

    try {
      paymentInFlightRef.current = true;
      setIsProcessing(true);
      setRestaurantButtonLoading?.(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (!selectedPayment && paymentVisible) {
        message.warning(`${messages.warnings.payment.select_payment_method}`);
        return;
      }
      const filtredCurrencies =
        tillData?.tillAccess?.csBunit?.currencies[0]?.denominations?.sort((a, b) => a.value - b.value) || [];
      const denominationsValue = filtredCurrencies[0]?.value || 1;
      const scale = 100;
      const scaledDividend = Math.round(amount * scale);
      const scaledDivisor = Math.round(denominationsValue * scale);
      const scaledRemainder = scaledDividend % scaledDivisor;
      const remainder = scaledRemainder / scale;
      const isReturnFlow = selectedPaymentMethod?.isReturns === "Y";
      const isGiftCardOrLoyalty =
        selectedPaymentMethod?.name?.toLowerCase() !== "loyalty" &&
        selectedPaymentMethod?.name?.toLowerCase() !== "voucher" &&
        selectedPaymentMethod?.name?.toLowerCase() !== "gift card";

      if (isGiftCardOrLoyalty && paymentVisible) {
        if (parseFloat(Math.abs(cart.total)) > parseFloat(cart.paid)) {
          if (selectedPaymentMethod?.name?.toLowerCase() === "cash" && remainder !== 0) {
            message.warning(`${messages.warnings.payment.payment_multiple_check} ${denominationsValue}`);
          } else {
            setNumb(0);
            if (
              !isReturnFlow &&
              selectedPaymentMethod?.name?.toLowerCase() !== "cash"
            ) {
              const chargesTotal = (cart.tip || 0) + (cart.serviceCharge || 0) + (cart.packingCharge || 0) + (cart.deiveryCharge || cart.deliveryCharge || 0);
              if (Number((cart.paid + parseFloat(amount)).toFixed(prcPrecision)) > Number((cart.total + chargesTotal).toFixed(prcPrecision))) {
                message.warning(`${messages.errors.cart.overpayment_not_allowed}`);
                return;
              }
            }
            // TSK-2934: paymentMethod.enablePaymentData (per-payment-method, from
            // getTillData) is a different flag from posConfig.enablePaymentData
            // (Till-level, used elsewhere in this file for Card only).
            if (selectedPaymentMethod?.enablePaymentData && selectedPaymentMethod?.paymentDataFields?.length > 0) {
              setPaymentDataPopupOpen(true);
              return;
            }
            requestPayment(selectedPaymentMethod, amount);
            setSelectedPayment(false);
            if (useRestaurantStore.getState().dineIn === "Y") {
              await markTableBillRequested();
            }
            await handleOrderCompletion(amount);
          }
        } else {
          processOrder();
        }
      } else if (paymentVisible === false) {
        setPaymentVisible(true);
      }
    } catch (error) {
      console.error("Payment Error:", error);
    } finally {
      paymentInFlightRef.current = false;
      setIsProcessing(false);
      setRestaurantButtonLoading?.(false);
    }
  };

  // TSK-2934: resumes the same tail handleEnterPayment would have run, after
  // the cashier fills in the configured payment-data fields.
  const handlePaymentDataConfirm = async (paymentData) => {
    if (paymentInFlightRef.current) return;
    setPaymentDataPopupOpen(false);
    try {
      paymentInFlightRef.current = true;
      setIsProcessing(true);
      setRestaurantButtonLoading?.(true);
      const activeCart = useCartStore.getState().cart || cart;
      // F-07: write through setCart instead of mutating the store object in place,
      // and pass the updated cart straight to requestPayment (as updatedCart) since
      // the `cart` closure variable here won't reflect this setCart until next render.
      const updatedCart = {
        ...activeCart,
        paymentReferenceData: { ...(activeCart.paymentReferenceData || {}), [selectedPaymentMethod.finPaymentmethodId]: paymentData },
      };
      setCart(updatedCart);
      await requestPayment(selectedPaymentMethod, amount, undefined, undefined, undefined, updatedCart);
      setSelectedPayment(false);
      if (useRestaurantStore.getState().dineIn === "Y") {
        await markTableBillRequested();
      }
      await handleOrderCompletion(amount);
    } catch (error) {
      console.error("Payment Error:", error);
    } finally {
      paymentInFlightRef.current = false;
      setIsProcessing(false);
      setRestaurantButtonLoading?.(false);
    }
  };

  return (
    <div className="checkout-root">
      <Row className="checkout-header-row" style={{ padding: "2vh 1.5vw 0 1.5vw", fontSize: "1vw" }}>
        <Col span={24}>
<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: 500,
    paddingLeft: "3px",
    fontSize: "1.25em",
    color: "#0F0718",
  }}
>
            <img
              src={Back}
              style={{ height: "4vh", cursor: "pointer", paddingBottom: "0.1em" }}
              onClick={() => {
                setPaymentVisible(false);
                setSelectedPayment(false);
                setSelectedPaymentMethod({});
              }}
            />{" "}
            Order Checkout
          </div>
        </Col>
      </Row>

      <Row className="checkout-body-row" style={{ textAlign: "center", fontSize: "1vw" }}>
        <Col span={24} style={{ fontSize: "1.5em", color: "#0F0718", fontWeight: 500 }}>
          {getMultilingualText("Amount To Pay")}{" "}
          {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
          {(() => {
            const total = cart.total || 0;
            const paid = cart.paid || 0;
            const tip = cart.tip || 0;
            const serviceCharge = cart.serviceCharge || 0;
            const packingCharge = cart.packingCharge || 0;
            const deliveryCharge = cart.deiveryCharge || cart.deliveryCharge || 0;
            const amountToPay = Math.abs(total) - paid + tip + serviceCharge + packingCharge + deliveryCharge;
            return amountToPay.toFixed(prcPrecision);
          })()}
          {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
        </Col>

        <Col className="checkout-spacer-col" span={5}></Col>
        <Col className="checkout-main-col" span={14}>
          <Row className="checkout-payment-methods-row" justify="center" style={{ padding: "5% 1% 0 0%", height: "18.5vh" }} gutter={5}>
            {sortBy(showPayments, "sequenceNo").map((pm, ind) => {
              if (
                (posConfig?.loyaltyApplicable === "N" && pm.isloyalty) ||
                (!cart?.customer?.loyaltyLevel?.cwrLoyaltyLevelId && pm.isloyalty) ||
                (!cart?.customer?.iscredit && pm.iscredit) ||
                pm.name.toLowerCase() === "cafe tips"
              ) {
                return null;
              }
              return (
                <Col span={6} key={pm.finPaymentmethodId}>
                  <button
                    onClick={() => {
                      if (cart.items.length > 0) {
                        setSelectedPayment(true);
                        let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || {
                          orderStartTime: "",
                          orderEndTime: "",
                          paymentStartTime: "",
                        };
                        if (orderTimeDetails.paymentStartTime === "") {
                          orderTimeDetails = {
                            ...orderTimeDetails,
                            paymentStartTime: dayjs(new Date()).format(userPreferences.dateTimeFormat),
                          };
                          usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
                        }
                        if (pm.name.toLowerCase() === "card" && !pm.integratedPayment && posConfig?.enablePaymentData === "Y") {
                          setIsCardPaymentFlag(true);
                        }
                        const additionalCharges = (cart.tip || 0) + (cart.serviceCharge || 0) + (cart.packingCharge || 0) + (cart.deiveryCharge || cart.deliveryCharge || 0);
                        const originalAmount = Math.abs(cart.total) - (cart.paid ?? 0) + additionalCharges;
                        const currenciesListLocal =
                          tillData?.tillAccess?.csBunit?.currencies?.[0]?.denominations?.sort((a, b) => a.value - b.value) || [];
                        const precision = prcPrecision;
                        const smallestDenomination = currenciesListLocal.length > 0 ? Number(currenciesListLocal[0].value) : 1;
                        if (
                          pm.name.toLowerCase() === "cash" &&
                          pm.roundOffForPos === "Y" &&
                          pm.integratedPayment === false
                        ) {
                          const roundedAmount = Math.ceil(originalAmount / smallestDenomination) * smallestDenomination;
                          setAmount(Number(roundedAmount).toFixed(precision));
                        } else {
                          setAmount(Number(originalAmount).toFixed(precision));
                        }
                        setSelectedPaymentMethod(pm);
                      }
                      setTimeout(() => {
                        paymentModalInputRef.current?.focus();
                        paymentModalInputRef.current?.select();
                      }, 100);
                    }}
                    id={`sm-payment-${pm.name.toLowerCase()}`}
                    className="checkout-payment-btn"
                    style={{
                      backgroundColor:
                        pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId ? "#2F3856" : "#fff",
                      border: "1px solid #fff",
                      borderRadius: "7px",
                      height: "16vh",
                      width: "8.2vw",
                      cursor: "pointer",
                    }}
                    tabIndex={0}
                    onKeyPress={(e) => (e.charCode === 13 ? setSelectedPaymentMethod(pm) : null)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "row",
                        position: "relative",
                        fontSize: "1vw",
                      }}
                    >
                      <span
                        className="payment"
                        style={{
                          color:
                            pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId ? "#fff" : "#0F0718",
                          fontSize: "1.5em",
                        }}
                      >
                        {pm.name.toLowerCase() === "cash" ? (
                          <>
                            <img
                              src={Cash1}
                              style={{
                                height: "3vh",
                                marginTop: "-4vh",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-7.5vh", left: "25%" }}>{pm.name}</p>
                          </>
                        ) : pm.name.toLowerCase() === "card" ? (
                          <>
                            <img
                              src={Card1}
                              style={{
                                height: "3.5vh",
                                marginTop: "-4vh",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-7.5vh", left: "25%" }}>{pm.name}</p>
                          </>
                        ) : pm.name.toLowerCase() === "upi" ? (
                          <>
                            <img
                              src={qrCodeIcon}
                              style={{
                                height: "3.5vh",
                                marginTop: "-4vh",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-6.5vh", left: "30%" }}>{pm.name}</p>
                          </>
                        ) : pm.iscredit ? (
                          <>
                            <img
                              src={Credit}
                              style={{
                                height: "3vh",
                                marginTop: "-4vh",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-6.5vh", left: "15%" }}>{pm.name}</p>
                          </>
                        ) : pm.name.toLowerCase() === "loyalty" ? (
                          <>
                            <img
                              src={Loyality}
                              style={{
                                height: "2.5vh",
                                left: "0.5em",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-7.5vh", left: "25%" }}>{pm.name}</p>
                          </>
                        ) : pm.name.toLowerCase() === "gift card" ? (
                          <>
                            <img
                              src={Gift}
                              style={{
                                height: "2.5vh",
                                marginBottom: "0.2em",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-7.5vh", left: "25%" }}>{pm.name}</p>
                          </>
                        ) : pm.name.toLowerCase() === "gift voucher" ? (
                          <>
                            <img
                              src={giftVoucher}
                              style={{
                                height: "2.5vh",
                                marginBottom: "0.2em",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-7.5vh", left: "25%" }}>{pm.name}</p>
                          </>
                        ) : pm.name.toLowerCase() === "credit note" ? (
                          <>
                            <img
                              src={creditNote}
                              style={{
                                height: "2.5vh",
                                filter:
                                  pm.finPaymentmethodId === selectedPaymentMethod.finPaymentmethodId
                                    ? "invert(1)"
                                    : "invert(0)",
                              }}
                            />
                            <p style={{ position: "absolute", bottom: "-7.5vh", left: "25%" }}>{pm.name}</p>
                          </>
                        ) : (
                          <p style={{ left: "25%", textAlign: "center", margin: 0 }}>{pm.name}</p>
                        )}
                      </span>
                    </div>
                  </button>
                </Col>
              );
            })}

            {filteredPaymentMethods.length > 4 && (
              <Col span={4}>
                <button
                  onClick={() => {
                    setSelectedPaymentMethod({});
                    isShowingAll ? showPreviousItems() : showMoreItems();
                  }}
                  style={{
                    backgroundColor: "#2F3856",
                    border: "1px solid #fff",
                    borderRadius: "7px",
                    height: "16vh",
                    width: "7.5vw",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "row",
                    }}
                  >
                    <img style={{ height: "2.5vh", left: "0.5em" }} color={"#fff"} />
                    <span className="payment" style={{ color: "#fff", marginLeft: "0.25em" }}>
                      ...
                    </span>
                  </div>
                </button>
              </Col>
            )}
          </Row>

          <Row style={{ margin: "0 0 0 0 ", paddingTop: "10%" }}>
            <Input
              className="productSearch sm-payment-search totalInput"
              style={{
                borderRadius: "0.5vw",
                height: "7.3vh",
                margin: "0 1% 2.7% 3.5%",
                textAlign: "right",
                fontWeight: "500",
                fontSize: "2.5em",
                borderColor: "#fff",
                color: "#2F3856",
              }}
              value={amount}
              id="sm-total-amount-input"
              onChange={onChangeAmount}
              placeholder="Enter Amount"
              ref={paymentModalInputRef}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              readOnly={cart.items.length > 0 ? false : true}
              disabled={
                tillData.tillAccess.cwrTill.layAway === "Y" && tillData.tillAccess.cwrTill.payNow === "N"
                  ? true
                  : false
              }
              onPressEnter={handleEnterPayment}
            />
          </Row>

          {denaminationsKeyboard && selectedPaymentMethod?.name?.toLowerCase() === "cash" ? (
            <Row style={{ padding: "0 1% 0 3.5%" }} gutter={[8, 6]}>
              <Col span={18}>
                <Row gutter={[8, 6]} style={{ display: "flex", flexDirection: "row" }}>
                  {currenciesData
                    .sort((a, b) => b.seqNo - a.seqNo)
                    .slice(showAll ? 11 : 0, showAll ? currenciesData.length : 11)
                    .map((currency, index) => (
                      <Col span={8} key={index}>
                        <button {...KEYPAD_NO_FOCUS}
                          id={`sm-amount-button${index + 1}`}
                          className="amt-dial-btn-restaurant"
                          onClick={() => handleCashPayment(currency.value)}
                          disabled={currency.value === 999999}
                          style={{ color: currency.value !== 999999 ? "#0f0718" : "transparent" }}
                        >
                          {currency.value}
                        </button>
                      </Col>
                    ))}
                  {currenciesData.length > 12 && (
                    <Col span={8}>
                      <button {...KEYPAD_NO_FOCUS}
                        id="sm-amount-buttonMore"
                        className="amt-dial-btn-restaurant"
                        onClick={() => setShowAll(!showAll)}
                      >
                        {showAll ? "Back" : "More..."}
                      </button>
                    </Col>
                  )}
                </Row>
              </Col>
              <Col span={6}>
                <Row style={{ padding: "0 0 7% 7%" }}>
                  <button {...KEYPAD_NO_FOCUS}
                    className="amt-dial-btn-restaurant"
                    id="sm-amount-buttonRemove"
                    onClick={() => handleAmount("x")}
                  >
                    <img
                      src={CancelArrow}
                      alt="back space"
                      width={width <= 1024 ? 20 : 25}
                      height={width <= 1024 ? 15 : 15}
                    />
                  </button>
                </Row>
                <Row style={{ padding: "0 0 7% 7%" }}>
                  <button {...KEYPAD_NO_FOCUS}
                    id="sm-amount-buttonClear"
                    className="amt-dial-btn-restaurant"
                    onClick={() => setDenaminationsKeyboard(!denaminationsKeyboard)}
                  >
                    1,2,3...
                  </button>
                </Row>
                <Row style={{ padding: "0 0 0 7%" }}>
                  <button {...KEYPAD_NO_FOCUS}
                    id="sm-amount-buttonEnter"
                    className="amt-dial-btn-restaurant"
                    style={{
                      background: (isProcessing || restaurantButtonLoading) ? "#ccc" : themeJSON?.keyBoardColor,
                      cursor: (isProcessing || restaurantButtonLoading) ? "not-allowed" : "pointer",
                      opacity: (isProcessing || restaurantButtonLoading) ? 0.6 : 1,
                    }}
                    disabled={isProcessing || restaurantButtonLoading}
                    onClick={handleEnterPayment}
                  >
                    Enter
                  </button>
                </Row>
              </Col>
            </Row>
          ) : (
            <Row style={{ padding: "0 1% 0 3.5%" }} gutter={[8, 6]}>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-button1"
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  onClick={() => handleAmount("1")}
                >
                  1
                </button>
              </Col>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-button2"
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  onClick={() => handleAmount("2")}
                >
                  2
                </button>
              </Col>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-button3"
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  onClick={() => handleAmount("3")}
                >
                  3
                </button>
              </Col>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  id="sm-amount-buttonRemove"
                  onClick={() => handleAmount("x")}
                >
                  <img src={CancelArrow} alt="back space" width={25} height={15} />
                </button>
              </Col>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-button4"
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  onClick={() => handleAmount("4")}
                >
                  4
                </button>
              </Col>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-button5"
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  onClick={() => handleAmount("5")}
                >
                  5
                </button>
              </Col>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-button6"
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  onClick={() => handleAmount("6")}
                >
                  6
                </button>
              </Col>
              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-buttonClear"
                  className="amt-dial-btn-restaurant"
                  style={{ background: themeJSON?.keyBoardColor }}
                  onClick={() => setDenaminationsKeyboard(!denaminationsKeyboard)}
                >
                  1,2,3...
                </button>
              </Col>

              <Col span={18}>
                <Row gutter={[8, 6]}>
                  <Col span={8}>
                    <button {...KEYPAD_NO_FOCUS}
                      id="sm-amount-button7"
                      className="amt-dial-btn-restaurant"
                      style={{ background: themeJSON?.keyBoardColor }}
                      onClick={() => handleAmount("7")}
                    >
                      7
                    </button>
                  </Col>
                  <Col span={8}>
                    <button {...KEYPAD_NO_FOCUS}
                      id="sm-amount-button8"
                      className="amt-dial-btn-restaurant"
                      style={{ background: themeJSON?.keyBoardColor }}
                      onClick={() => handleAmount("8")}
                    >
                      8
                    </button>
                  </Col>
                  <Col span={8}>
                    <button {...KEYPAD_NO_FOCUS}
                      id="sm-amount-button0"
                      className="amt-dial-btn-restaurant"
                      style={{ background: themeJSON?.keyBoardColor }}
                      onClick={() => handleAmount("9")}
                    >
                      9
                    </button>
                  </Col>
                  <Col span={8}>
                    <button {...KEYPAD_NO_FOCUS}
                      id="sm-amount-button0"
                      className="amt-dial-btn-restaurant"
                      style={{ background: themeJSON?.keyBoardColor }}
                      onClick={() => handleAmount("0")}
                    >
                      0
                    </button>
                  </Col>
                  <Col span={8}>
                    <button {...KEYPAD_NO_FOCUS}
                      id="sm-amount-button00"
                      className="amt-dial-btn-restaurant"
                      style={{ background: themeJSON?.keyBoardColor }}
                      onClick={() => handleAmount("00")}
                    >
                      00
                    </button>
                  </Col>
                  <Col span={8}>
                    <button {...KEYPAD_NO_FOCUS}
                      id="sm-amount-button."
                      className="amt-dial-btn-restaurant"
                      style={{ background: themeJSON?.keyBoardColor }}
                      onClick={() => handleAmount(".")}
                    >
                      .
                    </button>
                  </Col>
                </Row>
              </Col>

              <Col span={6}>
                <button {...KEYPAD_NO_FOCUS}
                  id="sm-amount-buttonEnter"
                  className="amt-dial-btn-restaurant-ent"
                  style={{
                    background: (isProcessing || restaurantButtonLoading) ? "#ccc" : themeJSON?.keyBoardColor,
                    cursor: (isProcessing || restaurantButtonLoading) ? "not-allowed" : "pointer",
                    opacity: (isProcessing || restaurantButtonLoading) ? 0.6 : 1,
                  }}
                  disabled={isProcessing || restaurantButtonLoading}
                  onClick={handleEnterPayment}
                >
                  Enter
                </button>
              </Col>
            </Row>
          )}
        </Col>
        <Col className="checkout-spacer-col" span={5}></Col>
      </Row>
      {paymentDataPopupOpen && (
        <PaymentDataPopup
          open={paymentDataPopupOpen}
          selectedPaymentMethod={selectedPaymentMethod}
          amount={amount}
          currencySymbol={currenciesList?.[0]?.currSymbol}
          onSubmit={handlePaymentDataConfirm}
          onCancel={() => setPaymentDataPopupOpen(false)}
        />
      )}
    </div>
  );
};

export default React.memo(PaymentCheckout);
